import {
  drop,
  get,
  has,
  find,
  forEach,
  findIndex,
  isString,
  isEmpty,
  some,
  take,
  sumBy,
  uniq,
  every,
  isBoolean,
} from 'lodash';
import pako from 'pako';

import OfferwallHomescreen from '../OfferwallHomescreen';
// eslint-disable-next-line import/no-cycle
import Terms from '../terms';
import views from '../views';
import selectors from '../selectors';

import {
  QUESTION_SINGLE,
  QUESTION_MULTIPLE,
  SURVEY_FORMAT_SHORT,
  QUESTION_NPS,
  QUESTION_MATRIX_MULTIPLE,
  QUESTION_MATRIX_SINGLE,
  ANSWER_OTHER,
  ANSWER_NONE_OF_THE_ABOVE,
  SHUFFLE_NONE,
  GROUP_TYPE_AB,
  GROUP_TYPE_BASIC,
  GROUP_TYPE_VAN_WESTENDORP,
  GROUP_TYPE_CONJOINT,
  GROUP_TYPE_MAXDIFF,
  TRANSFORMATION_DATA_TYPE_JSON,
  SURVEY_STATE,
  PARTICIPATION_STATUS,
} from '../../constants';
// eslint-disable-next-line import/no-cycle
import checkAndEnterSurvey from './checkAndEnterSurvey';

import QuestionGroup, { UNSUCCESSFUL_GROUP_SETUP } from './QuestionGroup';
import {
  transformAnswersForBackendStorage,
  transformAnswersForADL,
  transformValuesToString,
  encodeTextAnswers,
  shuffleArray,
} from '../../utils';
import {
  questionnaireLogicService,
  questionnairePathTraversal,
} from '../../logic';
import request from '../../request';
import { publicAssetsPrefix } from '../../../../../configuration';

class Survey {
  /* eslint-disable camelcase */
  constructor(
    {
      s_id: surveyId,
      questions = [],
      tag_questions: tagQuestions = [],
      sanity_questions: sanityQuestions = [],
      profiling_questions: profilingQuestions = [],
      lang,
      user_lang,
      survey_price,
      reward_name,
      reward_value,
      survey_class: surveyClass = '',
      survey_loi,
      survey_ir,
      remaining_completes,
      third_party_provider: thirdPartyProvider = null,
      isFeedbackSurvey = false,
      isOfferwallDemographics = false,
      distribution_id,
      survey_session_id,
      url,
      questionnaireLogic,
      respondentKnowledge,
      questionGroups,
      questionGroupShuffle,
      isPartialSurvey,
      hasCustomTerms = false,
      custom_end_message,
      is_distribution_link = false,
      is_external_audience = false,
      userTaxonomy,
      captchaProvider,
      captchaSiteKey,
      no_check: noCheck,
      surveyTheme,
    },
    options = {},
  ) {
    this.props = {
      surveyId,
      questions,
      tagQuestions,
      sanityQuestions,
      profilingQuestions,
      lang,
      user_lang,
      survey_price,
      reward_name,
      reward_value,
      surveyClass,
      survey_loi,
      survey_ir,
      remaining_completes,
      thirdPartyProvider,
      isFeedbackSurvey,
      isOfferwallDemographics,
      url,
      respondentKnowledge,
      questionGroups,
      questionGroupShuffle,
      distribution_id,
      surveySessionId: survey_session_id,
      isPartialSurvey,
      hasCustomTerms,
      custom_end_message,
      is_distribution_link,
      is_external_audience,
      captchaProvider,
      captchaSiteKey,
      noCheck,
      surveyTheme,
    };

    this.props.questionnaireLogic = questionnaireLogicService.migrate(
      questionnaireLogic,
    );
    /* eslint-enable camelcase */

    this.options = options;

    this.savedAnswers = [];
    this.questionOrder = -1;

    Pollfish.store.initRespondentKnowledge(respondentKnowledge, userTaxonomy);

    // handled question/answer ordering and some more
    this.preprocess();

    Pollfish.store.answeredQuestions = [];

    this.hasDownloadedImages = false;
    this.isDisqualifiedValue = false;

    // updates Pollfish.helper.json properties with received onces in case an override is needed
    // and i forgot to make it somewhere
    this.updateFallbackValues();

    // we generate a partial survey when a third party survey returns from a redirect on sdks,
    // where we are missing most of the survey information.
    // We needed a survey class instance to handle survey_session_id calls,
    // so we created a partial survey
    if (isPartialSurvey) {
      return this;
    }

    if (!isFeedbackSurvey) {
      // we separate screening questions from regular questions
      // because screening questions have no logic currently on them
      // and we need to handle those differently (at least for now)
      this.props.questions = questions.filter(
        ({ isScreening }) => !isScreening,
      );
      this.setupGroups();
      Pollfish.screening.setQuestions(questions);
      // keep these in case of feedback surveys so that we can continue from where we left off
      Pollfish.sanity.setQuestions(sanityQuestions);
      Pollfish.demographics.setQuestions(tagQuestions);
      Pollfish.profiling.setQuestions(profilingQuestions);
    }

    // update debug flags
    if (!Pollfish.helper.debug) {
      Pollfish.helper.debug = {};
    }
    Pollfish.helper.debug.questions = questions;
    // Seeing a feedback survey while answering demographics needs to show unlock screen again
    // i am using the debug tag questions to grab them again. this maybe needs some work... maybe
    // Pollfish.helper.debug.tag_questions = [];
    this.setupProgressBar();
    Pollfish.helper.lastSurveyId = surveyId;
    Pollfish.helper.isInternalSurvey =
      surveyClass.toLowerCase().indexOf('pollfish/internal') > -1;

    if (Pollfish.helper.isOfferwall()) {
      // needed for figuring out the success/error notifications
      Pollfish.pointsPage.pendingSurveyIds.push(surveyId);
    }

    return this;
  }

  async start({ startFromQuestion, sendAcceptedEvent = true } = {}) {
    Pollfish.ui.applyBrandedSurveyStyles();

    const { checkStatus = true } = this.options;
    const { surveyId, isFeedbackSurvey } = this.props;
    const surveyStyle = get(Pollfish, 'helper.json.theme.style');
    Pollfish.helper.getInitialHeight();
    // when starting a survey set the state as starting
    Pollfish.surveyState.setState(SURVEY_STATE.STARTING_SURVEY);
    Pollfish.surveyState.setFailedOutcomeReason(false);
    if (Pollfish.helper.closeButtonHidden) {
      Pollfish.ui.showCloseButtonAtHeader();
    }
    if (surveyStyle === SURVEY_FORMAT_SHORT) {
      Pollfish.shortSurveys.present();
      Pollfish.shortSurveys.results();
      return;
    }

    if (surveyId && !isFeedbackSurvey && sendAcceptedEvent) {
      let captchaToken;
      let captchaEkey;
      let captchaProvider;
      if (Pollfish.helper.shouldVerifyWithCaptchaOnSurveyAccept()) {
        Pollfish.ui.showSurveyLoading();
        try {
          ({
            token: captchaToken,
            ekey: captchaEkey,
          } = await Pollfish.helper.handleCaptcha({
            flow: 'enter_survey',
            surveyId,
          }));
          ({ captchaProvider } = Pollfish.survey.getCaptchaConfiguration());
        } catch (e) {
          this.handleAsyncError();
        }
      }
      const payload = {
        s_id: surveyId.toString(),
        distribution_id: this.getDistributionId().toString(),
      };

      if (captchaToken) {
        payload.captchaToken = captchaToken;
      }
      if (captchaEkey) {
        payload.captchaEkey = captchaEkey;
      }
      if (captchaProvider) {
        payload.captchaProvider = captchaProvider;
      }
      Pollfish.mobile.interface.sendToServer(
        '/v2/device/set/survey/accept',
        payload,
        false,
      );
    }

    this.downloadImages();
    this.getFirstStartingQuestion({ startFromQuestion })
      .then((question) => {
        // https://pollfish.atlassian.net/wiki/spaces/PUB/pages/1287782444/Survey+Distribution+Answering+events+flow
        // TL;DR: when a user hasn't completed demographics yet,
        // we allow them to answer them before calling check or get similar
        const shouldCheckStatus =
          Pollfish.demographics.isCompleted() &&
          Pollfish.profiling.isCompleted() &&
          checkStatus;
        // we only run check or get surveys for regular pollfish surveys
        if (shouldCheckStatus) {
          checkAndEnterSurvey({
            onSurveyContinue: () =>
              this.redirectTo3rdPartyOrShowQuestion(question),
          });
          return;
        }
        this.redirectTo3rdPartyOrShowQuestion(question);
      })
      .catch((error) => {
        if (error === UNSUCCESSFUL_GROUP_SETUP) {
          Pollfish.helper.handleUnsuccesfullGroupSetup(error);
        } else {
          Pollfish.helper.handleGlobalPromiseError(error);
        }
      });
  }

  // eslint-disable-next-line class-methods-use-this
  handleAsyncError(error) {
    // we get inside here when error occurs in captcha setup
    // in this case we want to show the expired screen
    // and do not ban the respondent through enter-survey
    // eslint-disable-next-line
    console.error(error);
    Raven.captureException(error);
    Pollfish.helper.handleSurveyComplete({
      isUserNotEligible: true,
      disqualificationReason: PARTICIPATION_STATUS.SURVEY_EXPIRED,
      shouldCallSurveyComplete: false,
      // shouldGetInRouter,
    });
  }

  async getFirstStartingQuestion({ startFromQuestion }) {
    if (!startFromQuestion) {
      return this.getNextQuestion();
    }
    const question = startFromQuestion;
    const questionIndex = findIndex(
      this.props.questions,
      ({ id }) => id === question.id,
    );
    if (questionIndex === -1) {
      const error = new Error(
        'Attempt to start from a question that does not exist',
      );
      Raven.captureException(error, {
        extra: Pollfish.helper.getErrorObject(),
      });
    }
    // we should also clear demographics, profiling and sanity questions here
    Pollfish.demographics.setQuestions([]);
    Pollfish.sanity.setQuestions([]);
    Pollfish.profiling.setQuestions([]);
    Pollfish.store.answeredQuestions = new Array(questionIndex);
    if (this.groups) {
      if (this.isRandomizeQuestionnaireEnabled) {
        // find the group that this question exists in
        // get its index from the groups array
        const questionGroupIndex = findIndex(this.groups, (group) =>
          find(group.questions, ['refId', question.refId]),
        );

        const questionGroup = this.groups[questionGroupIndex];
        // remove pre-pending groups and the current group from the new list
        const remainingGroups = drop(this.groups, questionGroupIndex);
        const removedGroups = take(this.groups, questionGroupIndex);
        const removedQuestions = sumBy(removedGroups, 'questions.length');
        const questionIndexInGroup = findIndex(questionGroup.questions, [
          'refId',
          question.refId,
        ]);
        // we also remove the question that we will be showing from the group
        questionGroup.questions = drop(
          questionGroup.questions,
          questionIndexInGroup,
        );
        this.currentGroup = questionGroup;
        this.groups = remainingGroups;
        const skippedQuestions = questionIndexInGroup + removedQuestions;
        Pollfish.store.answeredQuestions = new Array(skippedQuestions);
      } else if (
        !this.isRandomizeQuestionnaireEnabled &&
        has(question, 'groupRefId')
      ) {
        const group = find(this.groups, ['refId', question.groupRefId]);
        this.currentGroup = group;
      }
    }
    return question;
  }

  createOfferwallHomescreenSingleton = () => {
    if (!this.offerwallHomescreenInstance) {
      this.offerwallHomescreenInstance = new OfferwallHomescreen({
        onStartSurvey: () => {
          this.start().catch(Pollfish.helper.handleError);
        },
        onNoThanks: () => {
          // eslint-disable-next-line no-alert
          alert('No thanks');
        },
      });
    }
    return this.offerwallHomescreenInstance;
  };

  showOfferwallHomescreen = () => {
    const offerwallHomescreen = this.createOfferwallHomescreenSingleton();
    const homescreenRender = offerwallHomescreen.render();

    $('body').attr('data-page', views.common.surveyStartScreen);
    Pollfish.ui.applyMainContentTemplate(homescreenRender);

    const termsOptions = {
      elem: selectors.termsLink,
      moveBackBtn: $(selectors.moveBackBtn),
      currentPage: views.basic.terms,
      mainContent: $(selectors.mainContent),
      prevPage: views.common.surveyStartScreen,
    };

    offerwallHomescreen.attachHandlers();
    new Terms(termsOptions).render();
  };

  setupGroups() {
    const { questionGroupShuffle, questionnaireLogic } = this.props;
    let { questions, questionGroups = [] } = this.props;
    if (questionGroupShuffle && questionGroupShuffle !== SHUFFLE_NONE) {
      this.isRandomizeQuestionnaireEnabled = true;
    } else {
      this.isRandomizeQuestionnaireEnabled = false;
    }

    if (this.isRandomizeQuestionnaireEnabled && !isEmpty(questionnaireLogic)) {
      const error = new Error(
        'Unexpected questionnaireLogic when questionGroupShuffle is enabled',
      );
      Pollfish.helper.handleError(error);
      return;
    }

    if (!this.isRandomizeQuestionnaireEnabled && isEmpty(questionGroups)) {
      // nothing to do, move along
      return;
    }

    // when the randomization feature is enabled, then we need to add every question
    // into a group, to take advantage from the groupingStrategy logic
    // So the code below will transform a questionnaire where there are
    // [Question1, Question2, Group(Question 3)] into
    // [Group1(Question1), Group2(Question2), Group3(Question3)]
    if (this.isRandomizeQuestionnaireEnabled) {
      let groupIndex = 1;
      const transformedGroups = [];
      const transformedQuestions = [];
      questions.forEach((question) => {
        if (!question.groupRefId) {
          const newGroupId = groupIndex;
          const newGroupRefId = groupIndex.toString();
          const newGroup = {
            id: newGroupId,
            refId: newGroupRefId,
            shuffle: SHUFFLE_NONE,
            type: GROUP_TYPE_BASIC,
          };
          groupIndex += 1;
          transformedQuestions.push({ ...question, groupRefId: newGroupRefId });
          transformedGroups.push(newGroup);
        } else {
          transformedQuestions.push(question);
        }
      });

      questions = transformedQuestions;
      // NOTE: this is a little dangerous but
      this.props.questions = questions;
      questionGroups = [...transformedGroups, ...questionGroups];
    }

    const questionsInGroups = questions.filter((question) =>
      has(question, 'groupRefId'),
    );
    const transformedGroups = [];
    const groupRefIds = [];

    forEach(questionsInGroups, (question) => {
      const { groupRefId } = question;
      if (!groupRefIds.includes(groupRefId)) {
        groupRefIds.push(groupRefId);
      }

      const transformedGroup = find(
        transformedGroups,
        ({ refId }) => refId === groupRefId,
      );
      if (!transformedGroup) {
        const group = find(questionGroups, ({ refId }) => refId === groupRefId);
        const questionGroup = new QuestionGroup({
          questions: [question],
          refId: groupRefId,
          id: group.id,
          shuffle: group.shuffle,
          attributeSet: group.attributeSet,
          type: group.groupType,
        });
        transformedGroups.push(questionGroup);
      } else {
        transformedGroup.addQuestion(question);
      }
    });

    // after we have finished creating the groups,
    // we need to shuffle the questions inside each group
    forEach(transformedGroups, (group) => {
      group.shuffleQuestions();
    });

    this.groupRefIds = groupRefIds;
    this.groups = shuffleArray(transformedGroups, questionGroupShuffle);
    if (Pollfish.helper.isDebugMode()) {
      this.setRandomisedConceptsForGroups();
    } else {
      // we are calling the backend to provide these values when we get each group
    }
  }

  setRandomisedConceptsForGroups() {
    this.groups.forEach((group) => {
      group.randomizeConcepts();
    });
  }

  // groups can be invalid until the check survey enter function is called (AB tests only)
  // NOTE: this is not used yet
  areGroupsValid() {
    const shouldCheckConcepts =
      this.isRegularPollfishSurvey() && !isEmpty(this.groups);
    if (!shouldCheckConcepts) {
      return true;
    }
    return every(this.groups, (group) => group.isValid());
  }

  redirectTo3rdPartyOrShowQuestion = (question) => {
    if (question) {
      Pollfish.ui.moveToQuestion(question);
      return;
    }
    if (this.is3rdParty()) {
      Pollfish.ui.show3rdPartySurvey();
      if (Pollfish.helper.isNative()) {
        // no need to show feedback screen here since the native environment
        // will use the same webview to redirect to the 3rd party provider
        return;
      }
      Pollfish.ui.showFeedbackSurvey();
      Pollfish.helper.listenForFocus();
    } else {
      Raven.captureMessage('no next question or url', {
        extra: { ...Pollfish.helper.getRedirectData() },
      });
      // @TODO: remove duplicate with thank you
      Pollfish.mobile.interface.setSurveyCompleted();

      // show thank you component on basic format
      Pollfish.ui.thankYouBasic();
    }
  };

  reset = async () => {
    this.questionOrder = -1;
    if (Pollfish.helper.isOfferwall && this.props.isOfferwallDemographics) {
      this.onContinueAfterReset();
      return;
    }

    checkAndEnterSurvey({
      onSurveyContinue: this.onContinueAfterReset,
    });
  };

  onContinueAfterReset = () => {
    // we need to verify that it won't call check status again since we already did
    this.options.checkStatus = false;
    this.isFinished = false;
    this.groupIndex = null;
    Pollfish.sanity.reset();
    Pollfish.demographics.reset();
    Pollfish.screening.reset();
    Pollfish.profiling.reset();
    this.savedAnswers = [];

    // reset each QuestionGroup if groups exist
    if (!isEmpty(this.groups)) {
      this.groups.forEach((group) => group.reset());
    }

    Pollfish.store.initRespondentKnowledge();
    Pollfish.store.answeredQuestions = [];
    this.isDisqualifiedValue = false;

    this.setupProgressBar();
    this.start({ sendAcceptedEvent: false }).catch(Pollfish.helper.handleError);
  };

  getLastQuestion = () => {
    // we don't want to show the last sanity for third party/mediation or demographics surveys
    if (
      this.is3rdParty() ||
      this.isDemographicSurvey() ||
      !Pollfish.sanity.isPending()
    ) {
      return null;
    }
    this.isFinished = true;
    // PUB-2049 add a sanity at the end of each survey but not on demographics surveys
    const finalSanity = Pollfish.sanity.getFinalSanity();
    return finalSanity;
  };

  downloadImages() {
    // TODO: if survey has expired, previous downloads is useless
    for (let i = 1; i < this.props.questions.length; i += 1) {
      const question = this.props.questions[i];
      this.downloadImagesForQuestion(question, i);
    }
  }

  downloadImagesForQuestion(question, questionIndex = 0) {
    const questionImageToShow =
      question.optimized_image_url || question.image_url;
    const hasImageToShow = Boolean(questionImageToShow);

    // download question image if it exists
    if (hasImageToShow) {
      const questionImage = new Image();
      questionImage.src = questionImageToShow;
    }

    // download answer images if they exist
    for (let i = question.answers.length - 1; i >= 0; i -= 1) {
      const answer = question.answers[i];
      const answerDefaultImage = answer.optimized_image_url || answer.image_url;
      if (answerDefaultImage) {
        const answerImage = new Image();
        answerImage.src = answerDefaultImage;
      }
    }

    // download question poster if video exists
    if (question.video) {
      const questionPoster = new Image();
      questionPoster.src = question.video.poster;
    }

    // download images only once the first question has been answered
    if (questionIndex > 0 && !this.hasDownloadedImages) {
      const orcaImage = new Image();
      orcaImage.src = `${publicAssetsPrefix}/img/orca.png`;

      const searchImage = new Image();
      searchImage.src = `${publicAssetsPrefix}/img/search.png`;

      const thankYouImage = new Image();
      thankYouImage.src = `${publicAssetsPrefix}/img/thankyou.png`;

      this.hasDownloadedImages = true;
    }
  }

  // download media image (AB Testing) selectedAttribute if groups exist
  // eslint-disable-next-line class-methods-use-this
  downloadSelectedAttributesImagesForQuestionGroup(questionGroup) {
    const { selectedConcepts, concepts, questions } = questionGroup;
    if (!selectedConcepts || !concepts) {
      return;
    }
    const { attributeValues } = find(concepts, ['refId', selectedConcepts[0]]);
    let conceptSelectedAttributes = [];
    questions.forEach(({ selectedAttributes }) => {
      if (selectedAttributes) {
        conceptSelectedAttributes = [
          ...conceptSelectedAttributes,
          ...selectedAttributes,
        ];
      }
    });
    conceptSelectedAttributes = uniq(conceptSelectedAttributes);
    conceptSelectedAttributes.forEach((conceptReferenceAttributeId) => {
      const attributeValue = find(attributeValues, [
        'attributeRefId',
        conceptReferenceAttributeId,
      ]);
      if (!isEmpty(attributeValue.media)) {
        const mediaAttributeImg = new Image();
        mediaAttributeImg.src = attributeValue.media.thumbnail;
      }
    });
  }

  setDisqualified(value) {
    this.isDisqualifiedValue = value;
  }

  isDisqualified() {
    return this.isDisqualified;
  }

  isFeedbackSurvey() {
    return this.props.isFeedbackSurvey;
  }

  isDemographicSurvey() {
    return this.props.surveyClass === 'Pollfish/Demographics';
  }

  getSurveyTheme() {
    return get(this.props, 'surveyTheme');
  }

  isDistributionLink() {
    return this.props.is_distribution_link;
  }

  isExternalAudience() {
    return this.props.is_external_audience;
  }

  isRegularPollfishSurvey() {
    const { surveyClass } = this.props;
    return (
      surveyClass === 'Pollfish/Basic' ||
      surveyClass === 'Pollfish/Playful' ||
      surveyClass === 'Pollfish'
    );
  }

  setProps(newProps) {
    this.props = {
      ...this.props,
      ...newProps,
    };
  }

  isInternalSurvey() {
    const { surveyClass } = this.props;
    return surveyClass.toLowerCase().indexOf('pollfish/internal') > -1;
  }

  setupProgressBar() {
    const {
      questions,
      sanityQuestions,
      tagQuestions,
      profilingQuestions,
      questionnaireLogic,
    } = this.props;
    const screeningQuestions = Pollfish.screening.getQuestions() || [];
    const questionsLength = questionnaireLogic
      ? questionnairePathTraversal.findMaxLength(questionnaireLogic)
      : questions.length;

    let numberOfSanityQuestions = 0;
    // 1 sanity at the start of the survey
    // 1 trap question
    // 1 sanity at the end of the survey
    // = max 3
    if (!isEmpty(sanityQuestions)) {
      numberOfSanityQuestions =
        sanityQuestions.length > 3 ? 3 : sanityQuestions.length;
    }

    Pollfish.store.totalQuestions =
      screeningQuestions.length +
      questionsLength +
      numberOfSanityQuestions +
      tagQuestions.length +
      profilingQuestions.length;
  }

  preprocess() {
    const { questions } = this.props;
    let totalWords = '';
    let surveyLOI;

    for (let i = 0; i < questions.length; i += 1) {
      const question = questions[i];
      totalWords += question.prompt;

      // re-order questionnaire only and not tags
      if (question.isTag === undefined) {
        question.answers.sort((a, b) => {
          if (a.order > b.order) {
            return 1;
          }
          return -1;
        });
      }

      // order answers for short surveys
      for (let j = 0; j < question.answers.length; j += 1) {
        const answer = question.answers[j];
        totalWords += answer.body;

        // fallback in case style was not written correctly for "Other" option
        if (
          (question.style === QUESTION_SINGLE ||
            question.style === QUESTION_MULTIPLE) &&
          answer.order === 102
        ) {
          answer.style = ANSWER_OTHER;
        }

        // fallback in case style was not written correctly for "None of the above" option
        if (
          (question.style === QUESTION_SINGLE ||
            question.style === QUESTION_MULTIPLE) &&
          answer.order === 103
        ) {
          answer.style = ANSWER_NONE_OF_THE_ABOVE;
        }
      }

      // sort scorepoints
      if (
        question.style === QUESTION_MATRIX_SINGLE ||
        question.style === QUESTION_MATRIX_MULTIPLE
      ) {
        question.scorepoints.sort((a, b) => {
          if (a.ord > b.ord) {
            return 1;
          }
          return -1;
        });
      }

      // customize NPS min and max texts
      if (
        question.style === QUESTION_NPS &&
        question.answers[0] &&
        question.answers[10]
      ) {
        const minAnswer = question.answers[0];
        const maxAnswer = question.answers[10];
        const minText = Pollfish.ui.getText('NPSmin');
        const maxText = Pollfish.ui.getText('NPSmax');
        // NOTE: we have a case where the same survey is rendered twice and
        // the text was wrong
        if (minAnswer.body.indexOf(minText) === -1) {
          minAnswer.body = `${minAnswer.body} - ${minText}`;
        }
        if (maxAnswer.body.indexOf(maxText) === -1) {
          maxAnswer.body = `${maxAnswer.body} - ${maxText}`;
        }
      }

      // download images for first question only
      if (i === 0) {
        this.downloadImagesForQuestion(question);
      }
    }

    surveyLOI = Math.round(totalWords.split(' ').length / 220);

    if (surveyLOI > 4) {
      surveyLOI = 4;
    } else if (surveyLOI === 0) {
      surveyLOI = 1;
    }

    Pollfish.helper.surveyLOI = surveyLOI;
  }

  shuffleGroups() {
    const { questions } = this.props;

    return questions;
  }

  appendDemographics() {
    const { questions } = this.props;

    return questions;
  }

  // we currently only validate other type answers
  static validateAnswers(answers) {
    const errors = {};
    const emptyOtherAnswer = find(answers, (answer = {}) => {
      if (answer.type === ANSWER_OTHER) {
        const otherValue = $.trim($('#otherContainerInput').val());
        if (!otherValue) {
          return true;
        }
      }
      return false;
    });
    if (emptyOtherAnswer) {
      errors.emptyOtherAnswer = true;
    }
    return errors;
  }

  is3rdParty() {
    if (this.props.url && this.props.url.length > 0) {
      return true;
    }
    return false;
  }

  getRedirectURL() {
    const { url } = this.props;
    if (!url) {
      Raven.captureMessage(
        'Unexpected flow: survey redirection url should not be empty',
      );
      return undefined;
    }

    const hasQueryString = url.indexOf('?') !== -1;
    const urlEndsWithAmpersand = url.slice(url.length - 1) === '&';

    const surveySessionId = this.getSurveySessionIdAsQueryString();
    const profilingAnswers = Pollfish.profiling.getProfilingAnswersAsQueryString();

    if (!surveySessionId && !profilingAnswers) {
      return url;
    }

    const appendQueryString = [surveySessionId, profilingAnswers]
      .filter((queryString) => Boolean(queryString))
      .join('&');

    if (hasQueryString && urlEndsWithAmpersand) {
      return `${url}${appendQueryString}`;
    }
    if (hasQueryString && !urlEndsWithAmpersand) {
      return `${url}&${appendQueryString}`;
    }
    return `${url}?${appendQueryString}`;
  }

  hasExpired() {
    return Pollfish.helper.expiredSurveys.indexOf(this.props.surveyId) > -1;
  }

  getStoredAnswers() {
    // the backend expects screening question answers as well
    return [...Pollfish.screening.getStoredAnswers(), ...this.savedAnswers];
  }

  getQuestions() {
    return this.props.questions;
  }

  getQuestionByRefId(refId) {
    let questionRefId = refId;
    if (typeof refId !== 'string') {
      questionRefId = refId.toString();
    }

    // the order of these checks is by order of possible appearance so that
    // i try and minimize memory/cpu when searching. It's really minor though,
    // and not well thought out (ie: a survey might lack demographics)

    // if it is not a questionnaire question it can be a demographic question
    const tagQuestion = Pollfish.demographics.getQuestionByRefId(questionRefId);
    if (tagQuestion) {
      return tagQuestion;
    }

    // if it is not a questionnaire question it can be a profiling question
    const profilingQuestion = Pollfish.profiling.getQuestionByRefId(
      questionRefId,
    );
    if (profilingQuestion) {
      return profilingQuestion;
    }

    const screeningQuestion = Pollfish.screening.getQuestionByRefId(
      questionRefId,
    );
    if (screeningQuestion) {
      return screeningQuestion;
    }

    const question = find(this.props.questions, ['refId', questionRefId]);
    if (question) {
      return question;
    }

    // if it is not a questionnaire question it can be a sanity question
    const sanityQuestion = Pollfish.sanity.getQuestionByRefId(questionRefId);
    if (sanityQuestion) {
      return sanityQuestion;
    }

    return null;
  }

  getQuestionById(id) {
    let questionId = id;
    if (typeof questionId === 'string') {
      questionId = parseInt(questionId, 10);
    }

    // the order of these checks is by order of possible appearance so that
    // i try and minimize memory/cpu when searching. It's really minor though,
    // and not well thought out (ie: a survey might lack demographics)

    // if it is not a questionnaire question it can be a demographic question
    const tagQuestion = Pollfish.demographics.getQuestionById(questionId);
    if (tagQuestion) {
      return tagQuestion;
    }

    // if it is not a questionnaire question it can be a profiling question
    const profilingQuestion = Pollfish.profiling.getQuestionById(questionId);
    if (profilingQuestion) {
      return profilingQuestion;
    }

    const screeningQuestion = Pollfish.screening.getQuestionById(questionId);
    if (screeningQuestion) {
      return screeningQuestion;
    }

    const question = find(this.props.questions, ['id', questionId]);
    if (question) {
      return question;
    }

    // if it is not a questionnaire question it can be a sanity question
    const sanityQuestion = Pollfish.sanity.getQuestionById(questionId);
    if (sanityQuestion) {
      return sanityQuestion;
    }

    // throw new Error(
    // `tried finding question with id ${questionId} but no such question was found`);
    return null;
  }

  // NOTE: bear in mind that production sanity questions will include
  //       similar _answer_ ids. This was needed since we are now generating
  //       questions from a template and creating ids was cumbersome
  getAnswerById(id, questionContainingAnswer) {
    let answerId = id;
    if (typeof answerId === 'string') {
      answerId = parseInt(answerId, 10);
    }
    // this util is fairly slow and shouldn't be used much
    if (!questionContainingAnswer) {
      let answer;
      // early return if answer is found
      some(this.props.questions, (question) => {
        answer = find(question.answers, ['id', answerId]);
        return answer;
      });

      // check tag questions
      if (!answer) {
        // early return if answer is found
        some(Pollfish.screening.getQuestions(), (question) => {
          answer = find(question.answers, ['id', answerId]);
          return answer;
        });
      }

      // check tag questions
      if (!answer) {
        // early return if answer is found
        some(Pollfish.demographics.getQuestions(), (question) => {
          answer = find(question.answers, ['id', answerId]);
          return answer;
        });
      }

      // check sanity
      if (!answer) {
        // early return if answer is found
        some(Pollfish.sanity.getAllQuestions(), (question) => {
          answer = find(question.answers, ['id', answerId]);
          return answer;
        });
      }

      // check profiling
      if (!answer) {
        // early return if answer is found
        some(Pollfish.profiling.getAllQuestions(), (question) => {
          answer = find(question.answers, ['id', answerId]);
          return answer;
        });
      }

      return answer;
    }

    const answer = find(questionContainingAnswer.answers, ['id', answerId]);
    if (has(questionContainingAnswer, 'groupRefId')) {
      const group = find(this.groups, [
        'refId',
        questionContainingAnswer.groupRefId,
      ]);
      // since we show conceptsAnswers and not answers, we need to search for them in
      // a different place but we still support the "none of the above" option if existing
      const answerType = get(answer, 'style');
      if (
        group.type === GROUP_TYPE_CONJOINT &&
        answerType !== ANSWER_NONE_OF_THE_ABOVE
      ) {
        return find(group.currentChoiceSet.conceptAnswers, ['id', answerId]);
      }
      if (
        group.type === GROUP_TYPE_MAXDIFF &&
        answerType !== ANSWER_NONE_OF_THE_ABOVE
      ) {
        return find(group.currentChoiceSet.conceptAnswers, ['id', answerId]);
      }
    }
    return answer;
  }

  hasStartedSurvey() {
    return !isEmpty(this.savedAnswers);
  }

  // see confluence page to understand the expected business flow sanity + demographics
  // placements within a survey
  // https://pollfish.atlassian.net/wiki/spaces/PUB/pages/1153990657/Sanity+Checks
  getNextHelperQuestion(selectedAnswers, currentQuestion = {}) {
    // we currently do not support more than one feedback question
    // I could make this more "clever" by allowing future feedback questions
    // but i am not 100% sure how the rest of the application will behave with more
    // of them
    if (this.props.isFeedbackSurvey) {
      if (!selectedAnswers) {
        return this.props.questions[0];
      }

      return null;
    }

    // !selectedAnswer probably means that this is the first question of the survey,
    // we need to double check it doesn't happen in any other case.
    // Bear in mind that the old code was doing the same thing i just do not know
    // if there is an extra call to getNextQuestion now

    // always show a sanity as the first question of a survey if they exist
    if (
      Pollfish.sanity.isPending() &&
      Pollfish.sanity.getAnswersCount() === 0
    ) {
      return Pollfish.sanity.getNextQuestion();
    }

    // finish up with the demographic questions before moving on to profiling question
    if (Pollfish.demographics.isPending()) {
      return Pollfish.demographics.getNextQuestion();
    }

    // finish up with the profiling questions before moving on to the survey
    if (Pollfish.profiling.isPending()) {
      return Pollfish.profiling.getNextQuestion(currentQuestion);
    }

    // We finished with the demographics and we need to show either a screening
    // or a sanity TRAP question (if no screening questions exist)
    // show a sanity question after the first screening if a screening exists
    // or show the sanity question right after the first sanity
    if (
      Pollfish.sanity.hasTrapQuestion() &&
      !Pollfish.sanity.hasAnsweredTrapQuestion()
    ) {
      if (
        Pollfish.screening.isPending() &&
        Pollfish.screening.getAnswersCount() === 0
      ) {
        return Pollfish.screening.getNextQuestion();
      }
      return Pollfish.sanity.getNextTrapQuestion();
    }

    // continue with all the screeners
    // there is a case where we have jumped over the screeners
    // ie: a user has already answered _some_ survey questions
    // or we are using the preview. This is kind of a hack and we should handle it
    // differently
    if (!this.hasStartedSurvey() && Pollfish.screening.isPending()) {
      return Pollfish.screening.getNextQuestion();
    }

    // we need to send something different than null and falsy
    return undefined;
  }

  async getNextQuestion(
    selectedAnswers,
    currentQuestion = {},
    selectedConceptIds,
  ) {
    if (this.isFinished) {
      return null;
    }
    const { questionnaireLogic } = this.props;

    const nextHelperQuestion = this.getNextHelperQuestion(
      selectedAnswers,
      currentQuestion,
    );
    // we also need to handle the case where we really shouldn't return anything
    if (nextHelperQuestion || nextHelperQuestion === null) {
      return nextHelperQuestion;
    }

    if (this.isRandomizeQuestionnaireEnabled) {
      return this.getNextQuestionByGroupStrategy();
    }

    // NOTE: I don't like how this fallback has been implemented here. We/I should probably
    // rewrite it
    if (!questionnaireLogic) {
      // being here means we have exhausted screening/demographic/sanity/profiling
      // questions and we move towards the actual survey questions
      // setup the entry point of the survey based on the head id
      if (!this.hasStartedSurvey()) {
        // just show the first question
        return this.props.questions[0];
      }

      const nextQuestion = this.getNextQuestionWithoutADL(
        currentQuestion,
        selectedAnswers,
      );
      if (!nextQuestion) {
        return this.getLastQuestion();
      }
      return nextQuestion;
    }

    const knowledge = Pollfish.store.getRespondentKnowledge();
    // if the survey is not started and we have a questionnaireLogic object,
    // then grab the first entry point
    if (!this.hasStartedSurvey()) {
      // means that this is a user that has already answered some questions in the past
      if (!isEmpty(knowledge.questionAnswers)) {
        const answerOutcome = questionnaireLogicService.getOutcome(
          questionnaireLogic,
          knowledge,
        );
        const nextQuestionRefId = answerOutcome.params.id;
        if (!isEmpty(answerOutcome.reason)) {
          Pollfish.surveyState.setFailedOutcomeReason(answerOutcome.reason);
        }
        return this.getNextQuestionFromNode(nextQuestionRefId);
      }

      const answerOutcome = questionnaireLogicService.getNextOutcomeForEntryPoint(
        questionnaireLogic.entryPoint,
        {},
      );
      const nextQuestionRefId = answerOutcome.params.id;
      if (!isEmpty(answerOutcome.reason)) {
        Pollfish.surveyState.setFailedOutcomeReason(answerOutcome.reason);
      }
      return this.getNextQuestionFromNode(nextQuestionRefId);
    }

    let nextQuestionRefId;
    if (this.currentGroup) {
      nextQuestionRefId = this.getNextQuestionByGroupStrategyWithADL(
        selectedConceptIds,
      );
    } else {
      const answerOutcome = questionnaireLogicService.getOutcomeForNode(
        questionnaireLogic,
        knowledge,
        currentQuestion.refId,
      );
      if (!isEmpty(answerOutcome.reason)) {
        Pollfish.surveyState.setFailedOutcomeReason(answerOutcome.reason);
      }
      nextQuestionRefId = answerOutcome.params.id;
    }

    return this.getNextQuestionFromNode(nextQuestionRefId);
  }

  getNextQuestionWithoutADL(currentQuestion, selectedAnswers) {
    const passingAnswer = Pollfish.helper.getPassingAnswer(
      currentQuestion,
      selectedAnswers,
    );
    if (!passingAnswer) {
      return null;
    }
    // it's unlikely a question missing the refId but we handle this case just in case
    let currentQuestionIndex;
    if (currentQuestion.refId) {
      currentQuestionIndex = findIndex(this.props.questions, [
        'refId',
        currentQuestion.refId,
      ]);
    } else if (currentQuestion.id) {
      currentQuestionIndex = findIndex(this.props.questions, [
        'id',
        currentQuestion.id,
      ]);
    }

    if (currentQuestionIndex === -1) {
      throw new Error('Missing Question');
    }
    return this.props.questions[currentQuestionIndex + 1];
  }

  // a node can be either a QuestionGroup or a Question
  async getNextQuestionFromNode(nextNodeRefId) {
    const { questions } = this.props;
    let nextQuestionRefId = nextNodeRefId;
    // if the next refId node is zero means we reached the end of the survey
    // and should show the last sanity question
    if (nextNodeRefId === '0') {
      return this.getLastQuestion();
    }
    if (this.groupRefIds && this.groupRefIds.includes(nextNodeRefId)) {
      this.currentGroup = this.groups.find(
        (group) => group.refId === nextNodeRefId,
      );
      await this.currentGroup.setup();
      nextQuestionRefId = this.getNextQuestionByGroupStrategyWithADL();
    }

    const nextQuestion = find(questions, ['refId', nextQuestionRefId]);
    return nextQuestion;
  }

  getNextQuestionByGroupStrategyWithADL() {
    const { questionnaireLogic } = this.props;

    if (!this.currentGroup) {
      throw new Error(
        'Called getNextQuestionByGroupStrategyWithADL without a current group',
      );
    }

    if (this.currentGroup.isCompleted()) {
      const knowledge = Pollfish.store.getRespondentKnowledge();
      const answerOutcome = questionnaireLogicService.getOutcomeForNode(
        questionnaireLogic,
        knowledge,
        this.currentGroup.refId,
      );
      Pollfish.surveyState.setFailedOutcomeReason(answerOutcome.reason);
      const nextQuestionRefId = answerOutcome.params.id;
      this.currentGroup = undefined;
      return nextQuestionRefId;
    }

    const nextQuestion = this.currentGroup.getNextQuestion();
    return nextQuestion.refId;
  }

  async getNextQuestionByGroupStrategy() {
    const { groups } = this;
    if (!this.groupIndex) {
      this.groupIndex = 0;
    }

    if (this.currentGroup && this.currentGroup.isCompleted()) {
      this.currentGroup = undefined;
      this.groupIndex += 1;
    }

    if (!this.currentGroup && Boolean(groups[this.groupIndex])) {
      this.currentGroup = groups[this.groupIndex];
      await this.currentGroup.setup();
    }

    if (!this.currentGroup) {
      return null;
    }

    return this.currentGroup.getNextQuestion();
  }

  saveAnswer(question, answers) {
    let questionId = question.id;
    const { refId: questionRefId } = question;

    if (
      question.isTag &&
      isString(questionId) &&
      questionId.indexOf('tag_') > -1
    ) {
      // string question ids for isTag questions is an old way. I expect that this code is not being
      // called anymore.
      Pollfish.analytics.sendEvent(
        'shouldDeprecate',
        'tag_XX',
        'Tag questions should no longer have string ids',
      );
      questionId = questionId.replace('tag_', '');
    } else if (question.isSanityQuestion) {
      // we expect sanity questions coming from the backend will contain the sanity_ prefix
      questionId = isString(questionId)
        ? questionId.replace('sanity_', '')
        : questionId;
    }

    // store selected question to global array
    Pollfish.store.answeredQuestions.push(questionRefId || questionId);
    Pollfish.helper.checkTimeElapsed();

    const rawData = get(Pollfish, 'helper.json.raw', {});
    const interactions = get(Pollfish, 'event.interactions', []);
    // create param object to send to server
    // TBD: remove version but also inform backend
    const interactionsBinary = pako.deflate(JSON.stringify(interactions), {
      to: 'string',
    });
    const base64Interactions = btoa(interactionsBinary);
    const uriEncodedInteractions = encodeURIComponent(base64Interactions);
    const rewardConversionHash = rawData.reward_conversion_hash || '';
    this.questionOrder += 1;
    const requestObject = {
      s_id: Pollfish.helper.json.s_id,
      ttc: Pollfish.helper.ttc.elapsed,
      reward_name: rawData.reward_name
        ? encodeURIComponent(rawData.reward_name)
        : '',
      reward_conversion: rawData.reward_conversion || '',
      reward_conversion_hash: encodeURIComponent(rewardConversionHash),
      interactions: uriEncodedInteractions,
      token: Pollfish.helper.getNextCount(),
      q_id: questionId,
      q_ref_id: questionRefId,
      answers: transformAnswersForBackendStorage({
        question,
        answers,
      }),
      survey_session_id: this.getSurveySessionId(),
      questionOrder: this.questionOrder,
      questionStyle: question.style,
    };

    // NOTE: This will also send back disqualifying sanity answers. It is expected as of today
    if (question.isSanityQuestion) {
      Pollfish.sanity.saveAnswer(requestObject);
    } else if (question.isTag) {
      Pollfish.demographics.saveAnswer(requestObject);
    } else if (question.isProfilingQuestion) {
      Pollfish.profiling.saveAnswer(requestObject);
    } else if (question.isScreening) {
      Pollfish.screening.saveAnswer(requestObject, question, answers);
    } else {
      // this needs to be here, because we handle the survey start with the entry point
      // based on the questionsAnswered on the respondent knowledge object. We need to revisit
      // this in any case
      const knowledge = Pollfish.store.getRespondentKnowledge();
      const transformedAnswers = transformAnswersForADL({
        question,
        answers,
      });
      knowledge.questionAnswers.push({
        questionId: question.refId,
        answers: transformedAnswers,
      });

      if (has(question, 'groupRefId')) {
        const group = find(this.groups, ['refId', question.groupRefId]);
        if (
          group.type === GROUP_TYPE_AB ||
          group.type === GROUP_TYPE_VAN_WESTENDORP
        ) {
          const selectedConceptRefId = group.currentConcept;
          const selectedConcept = find(group.concepts, [
            'refId',
            selectedConceptRefId,
          ]);
          const selectedConceptId = selectedConcept.id.toString();
          requestObject.attribute_grouping_id = selectedConceptId;
          Pollfish.store.addAnsweredConcept(group.refId, selectedConcept);
        } else if (
          group.type === GROUP_TYPE_CONJOINT ||
          group.type === GROUP_TYPE_MAXDIFF
        ) {
          // do something for conjoint when we allow logic rules
          requestObject.choiceSetBlockId = group.block.id;
          requestObject.choiceSetId = group.currentChoiceSet.id;
          Pollfish.store.addAnsweredChoiceSet(
            group.refId,
            group.currentChoiceSet.id,
            group.block.id,
          );
        }
        // order here is important, since saveAnswer will change the "group.currentConcept" which
        // is used on the requestObject above
        group.saveAnswer(question, answers);
      }

      // we save these answers so that the backend will receive them all together
      // but the backend expects their values as strings s_id, ttc etc
      this.savedAnswers.push(
        encodeTextAnswers(transformValuesToString(requestObject)),
      );
      // send the selected answer to the server
      // we don't need to save the answer for feedback surveys, since we also send it on
      // the survey/reject endpoints
      if (
        !Pollfish.helper.userRejectsOnUnlockScreen() &&
        !this.isFeedbackSurvey()
      ) {
        request({
          url: '/v2/device/save/answers',
          method: 'POST',
          contentType: 'application/json; charset=utf-8',
          transformData: true,
          transformationDataType: TRANSFORMATION_DATA_TYPE_JSON,
          data: requestObject,
        });
      }
    }
  }

  getDistributionId() {
    return encodeURIComponent(this.props.distribution_id);
  }

  setSurveySessionId(id) {
    this.props.surveySessionId = id;
  }

  getSurveySessionId() {
    return this.props.surveySessionId;
  }

  getSurveyId() {
    return this.props.surveyId;
  }

  getCustomEndMessage() {
    return this.props.custom_end_message;
  }

  getSurveySessionIdAsQueryString() {
    const surveySessionId = this.getSurveySessionId();
    if (surveySessionId) {
      return `survey_session_id=${surveySessionId}`;
    }
    return '';
  }

  getSurveyLanguage() {
    return this.props.lang;
  }

  getCaptchaConfiguration() {
    return {
      siteKey: this.props.captchaSiteKey,
      captchaProvider: this.props.captchaProvider,
    };
  }

  setSurveyRedirectUrl(url) {
    if (!url) {
      return;
    }
    this.props.url = url;
  }

  updateFallbackValues() {
    /* eslint-disable camelcase */
    const {
      surveyId,
      surveyClass,
      thirdPartyProvider,
      reward_name,
      reward_value,
      reward_conversion,
      reward_conversion_hash,
      survey_price,
      survey_loi,
      survey_ir,
      remaining_completes,
      distribution_id,
      hasCustomTerms,
      // lang,
      // user_lang,
    } = this.props;
    Pollfish.helper.json.s_id = surveyId;
    Pollfish.helper.json.survey_class = surveyClass;
    Pollfish.helper.json.third_party_provider = thirdPartyProvider;
    Pollfish.helper.json.reward_name = reward_name;
    Pollfish.helper.json.reward_value = reward_value;
    Pollfish.helper.json.reward_conversion = reward_conversion;
    Pollfish.helper.json.reward_conversion_hash = reward_conversion_hash;
    Pollfish.helper.json.survey_loi = survey_loi;
    Pollfish.helper.json.survey_ir = survey_ir;
    Pollfish.helper.json.survey_price = survey_price;
    Pollfish.helper.json.remaining_completes = remaining_completes;
    Pollfish.helper.json.distribution_id = distribution_id;
    Pollfish.helper.json.hasCustomTerms = hasCustomTerms;
    if (this.isFeedbackSurvey()) {
      Pollfish.store.surveyRejectTriggerPage = $('body').attr('data-page');
    } else {
      Pollfish.store.surveyRejectTriggerPage = undefined;
    }
    // Pollfish.helper.json.user_lang = user_lang;
    // Pollfish.helper.json.lang = lang;
    /* eslint-disable camelcase */
  }

  getNoCheck() {
    const { noCheck } = this.props;
    if (isBoolean(noCheck)) {
      return noCheck;
    }
    return null;
  }
}

export default Survey;
